import { KeyResult } from "@/types";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OkrStatusBadge } from "./OkrStatusBadge";
import { KrPppList } from "./KrPppList";
import { UserAvatar } from "@/components/UserAvatar";
import { MoreHorizontal, Pencil, Trash2, ChevronRight, GripVertical } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { TREE_ROW_BASE, TREE_ROW_COLS } from "./okr-tree-constants";
import { okrPct } from "./okrConstants";
import { KrMetaBadges } from "./KrMetaBadges";
import { KrProgressPopover } from "./KrProgressPopover";
import { ContributorStack } from "./ContributorStack";
import { MarkdownRenderer } from "@/components/ui/MarkdownRenderer";

interface KeyResultCardProps {
    keyResult: KeyResult;
    onEdit?: (keyResult: KeyResult) => void;
    onDelete?: (keyResult: KeyResult) => void;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    compact?: boolean;
    /** KR writes are C-level only; hides the action menu otherwise */
    canManage?: boolean;
}

export function KeyResultCard({ keyResult, onEdit, onDelete, dragHandleProps, compact, canManage = false }: KeyResultCardProps) {
    const [expanded, setExpanded] = useState(false);
    const pct = okrPct(keyResult.progress, keyResult.targetProgress);

    const actionsMenu = canManage ? (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                    <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit?.(keyResult)}>
                    <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive" onClick={() => onDelete?.(keyResult)}>
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    ) : null;

    if (compact) {
        return (
            <div className="flex items-center gap-2 py-2 border-b border-border/40 last:border-0">
                <OkrStatusBadge status={keyResult.status} size="sm" />
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{keyResult.code}</span>
                <span className="text-[13px] truncate flex-1">{keyResult.name}</span>
                <KrMetaBadges keyResult={keyResult} />
                {canManage ? (
                    <KrProgressPopover keyResult={keyResult}>
                        <button type="button" onClick={e => e.stopPropagation()} aria-label="Update progress"
                            className="text-[11px] text-muted-foreground shrink-0 hover:text-foreground hover:underline decoration-dotted underline-offset-2">
                            {pct}%
                        </button>
                    </KrProgressPopover>
                ) : (
                    <span className="text-[11px] text-muted-foreground shrink-0">{pct}%</span>
                )}
                <ContributorStack contributors={keyResult.contributors} count={keyResult.contributorCount} />
                {keyResult.owner && (
                    <UserAvatar src={keyResult.owner.avatarUrl} name={keyResult.owner.fullName} className="h-5 w-5 shrink-0" />
                )}
                {actionsMenu}
            </div>
        );
    }

    return (
        <div>
            <div
                className={cn(TREE_ROW_BASE, "hover:bg-muted/20")}
                style={TREE_ROW_COLS}
            >
                {/* Left accent bar */}
                <div className={cn(
                    "absolute left-0 inset-y-1.5 w-[3px] rounded-r bg-blue-500 transition-opacity",
                    expanded ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                )} />

                {/* Name cell */}
                <div className="flex items-center min-w-0 gap-0">
                    {dragHandleProps && (
                        <button type="button" {...dragHandleProps}
                            onClick={e => e.stopPropagation()}
                            className="text-muted-foreground/40 hover:text-muted-foreground cursor-grab mr-1 flex-shrink-0">
                            <GripVertical className="h-3 w-3" />
                        </button>
                    )}
                    {/* 20px indent for KR level */}
                    <div className="w-5 flex-shrink-0" />
                    <button type="button"
                        aria-label={expanded ? "Collapse PPPs" : "Expand PPPs"}
                        className="h-[22px] w-[22px] flex-shrink-0 flex items-center justify-center rounded text-muted-foreground hover:bg-accent transition-colors mr-1"
                        onClick={() => setExpanded(v => !v)}>
                        <ChevronRight className={cn("h-3.5 w-3.5 transition-transform duration-200", expanded && "rotate-90")} />
                    </button>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide whitespace-nowrap mr-2 flex-shrink-0 bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                        KR
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap mr-2 flex-shrink-0 min-w-[40px]">
                        {keyResult.code}
                    </span>
                    <span className="text-[13px] font-medium truncate flex-1">{keyResult.name}</span>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        <KrMetaBadges keyResult={keyResult} />
                    </div>
                </div>

                {/* Status */}
                <div><OkrStatusBadge status={keyResult.status} size="sm" /></div>

                {/* Progress */}
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-[width] duration-300"
                            style={{ width: `${pct}%`, background: "linear-gradient(90deg, #3B82F6, #6366F1)" }} />
                    </div>
                    {canManage ? (
                        <KrProgressPopover keyResult={keyResult}>
                            <button type="button" aria-label="Update progress"
                                className="text-[11px] font-semibold text-muted-foreground w-7 text-right flex-shrink-0 hover:text-foreground hover:underline decoration-dotted underline-offset-2">
                                {pct}%
                            </button>
                        </KrProgressPopover>
                    ) : (
                        <span className="text-[11px] font-semibold text-muted-foreground w-7 text-right flex-shrink-0">
                            {pct}%
                        </span>
                    )}
                </div>

                {/* Owner */}
                <div className="flex items-center gap-1.5">
                    {keyResult.owner ? (
                        <>
                            <UserAvatar src={keyResult.owner.avatarUrl} name={keyResult.owner.fullName} className="h-[22px] w-[22px] flex-shrink-0" />
                            <span className="text-[11px] text-muted-foreground truncate">{keyResult.owner.fullName}</span>
                        </>
                    ) : <span className="text-[11px] text-muted-foreground">—</span>}
                    <ContributorStack contributors={keyResult.contributors} count={keyResult.contributorCount} className="ml-auto" />
                </div>

                {/* Actions */}
                <div className="flex justify-end">{actionsMenu}</div>
            </div>

            {expanded && (
                <>
                    {keyResult.context && (
                        <div className="ml-12 mr-2 my-2 rounded-md border bg-muted/30 p-3">
                            <MarkdownRenderer content={keyResult.context} className="text-[13px]" />
                        </div>
                    )}
                    <KrPppList keyResultPid={keyResult.pid} />
                </>
            )}
        </div>
    );
}
